import { KeyboardEvent, ReactNode, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  IconAdjustmentsHorizontal,
  IconAlertCircle,
  IconChevronLeft,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";

export type Tone = "good" | "warn" | "bad" | "info" | "neutral";

/**
 * Makes `value` the only inference site for a control's id type. Without it, a
 * `setState` passed as `onChange` offers `T | ((prev) => T)` and inference fails.
 */
type NoInfer<T> = [T][T extends any ? 0 : never];

export function PageHeader({
  title,
  subtitle,
  back,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: { to: string; label: string };
  actions?: ReactNode;
}) {
  return (
    <header className="sq-page-header">
      <div className="sq-page-heading">
        {back ? (
          <Link to={back.to} className="sq-back">
            <IconChevronLeft size={16} />
            {back.label}
          </Link>
        ) : null}
        <h1 className="sq-page-title">{title}</h1>
        {subtitle ? <p className="sq-page-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="sq-page-actions">{actions}</div> : null}
    </header>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  flush,
  id,
  className = "",
  children,
  ...rest
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Drop the body padding for tables and lists that run edge to edge. */
  flush?: boolean;
  id?: string;
  className?: string;
  children?: ReactNode;
  "aria-label"?: string;
}) {
  return (
    <section id={id} className={`sq-card ${className}`} {...rest}>
      {title || actions ? (
        <div className="sq-card-head">
          <div className="sq-card-heading">
            {title ? <h2 className="sq-card-title">{title}</h2> : null}
            {subtitle ? <p className="sq-card-sub">{subtitle}</p> : null}
          </div>
          {actions}
        </div>
      ) : null}
      {flush ? children : <div className="sq-card-body">{children}</div>}
    </section>
  );
}

export function Stat({
  label,
  value,
  unit,
  note,
  icon,
  tone,
  to,
  onClick,
  aside,
  footer,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  note?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  to?: string;
  onClick?: () => void;
  /** Rendered beside the label (a status pill, for example). */
  aside?: ReactNode;
  footer?: ReactNode;
}) {
  const className = `sq-stat${to || onClick ? " is-link" : ""}${tone ? ` tone-${tone}` : ""}`;
  const body = (
    <>
      <div className="sq-stat-top">
        <p className="sq-stat-label">
          {icon}
          {label}
        </p>
        {aside}
      </div>
      <p className="sq-stat-value">
        {value}
        {unit ? <span className="sq-stat-unit"> {unit}</span> : null}
      </p>
      {note ? <p className="sq-stat-note">{note}</p> : null}
      {footer}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={className}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}

export function Pill({
  tone = "neutral",
  icon,
  title,
  children,
}: {
  tone?: Tone;
  icon?: ReactNode;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span className={`sq-pill tone-${tone}`} title={title}>
      {icon}
      {children}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  compact,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={`sq-empty${compact ? " is-compact" : ""}`}>
      {icon}
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function ErrorBanner({
  children,
  onRetry,
}: {
  children: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="sq-error" role="alert">
      <span>
        <IconAlertCircle size={18} />
        <span>{children}</span>
      </span>
      {onRetry ? (
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="sq-skel-rows" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <span
          key={i}
          className="sq-skel"
          style={{ width: `${92 - ((i * 13) % 30)}%` }}
        />
      ))}
    </div>
  );
}

export function SkeletonStats({ count = 4 }: { count?: number }) {
  return (
    <div className="sq-stats" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="sq-stat">
          <span className="sq-skel" style={{ width: "60%" }} />
          <span className="sq-skel is-tall" style={{ width: "40%", marginTop: "0.5rem" }} />
        </div>
      ))}
    </div>
  );
}

/** Announces the loading state to screen readers while skeletons are on screen. */
export function LoadingPanel({ label, rows }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-label={label}>
      <SkeletonRows rows={rows} />
    </div>
  );
}

export function RefreshButton({
  onClick,
  loading,
  label = "Refresh",
}: {
  onClick: () => void;
  loading?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={onClick}
      disabled={loading}
      title={label}
    >
      <IconRefresh size={16} />
      <span className="sq-btn-label">{label}</span>
    </button>
  );
}

export function DownloadButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary"
      onClick={onClick}
      title={`Download ${label}`}
    >
      <IconDownload size={16} />
      <span className="sq-btn-label">{label}</span>
    </button>
  );
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
  block,
}: {
  options: ReadonlyArray<{ id: NoInfer<T>; label: ReactNode }>;
  value: T | null;
  onChange: (id: NoInfer<T>) => void;
  label: string;
  block?: boolean;
}) {
  return (
    <div className={`sq-seg${block ? " is-block" : ""}`} role="group" aria-label={label}>
      {options.map((opt) => (
        <button
          key={String(opt.id)}
          type="button"
          aria-pressed={value === opt.id}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ id: NoInfer<T>; label: ReactNode }>;
  value: T;
  onChange: (id: NoInfer<T>) => void;
  label: string;
}) {
  return (
    <div className="sq-chips" role="group" aria-label={label}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          className="sq-chip"
          aria-pressed={value === opt.id}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Tracks a CSS media query, re-rendering when it flips. */
export function useMediaQuery(query: string): boolean {
  const read = useCallback(
    () => (typeof window !== "undefined" ? window.matchMedia(query).matches : false),
    [query]
  );
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

export const PHONE_QUERY = "(max-width: 767px)";
export const TOUCH_QUERY = "(hover: none), (pointer: coarse), (max-width: 767px)";

/** Spreads onto a `<tr>` so clickable rows also work from the keyboard. */
export function activatable(handler: () => void, enabled = true) {
  if (!enabled) return {};
  return {
    onClick: handler,
    tabIndex: 0,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
      }
    },
  };
}

/** Filter fields: inline on desktop, behind a "Filters" button on phones. */
export function CollapsibleFilters({
  activeCount = 0,
  children,
}: {
  activeCount?: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary sq-filters-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <IconAdjustmentsHorizontal size={16} />
        {open ? "Hide filters" : "Filters"}
        {activeCount > 0 ? ` (${activeCount})` : ""}
      </button>
      <div className={`sq-filters-body${open ? " is-open" : ""}`}>{children}</div>
    </>
  );
}
